/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1672790400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jan 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "rudderstack",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Event types",
          "depth": 1,
          "id": "event-types"
        }, {
          "value": "User and anonymous IDs",
          "depth": 1,
          "id": "user-and-anonymous-ids"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Sending initial results (track only)",
          "depth": 1,
          "id": "sending-initial-results-track-only"
        }, {
          "value": "Event names and timestamps (generic track only)",
          "depth": 1,
          "id": "event-names-and-timestamps-generic-track-only"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      ul: "ul",
      strong: "strong"
    }, _provideComponents(), props.components), {EventSyncWarning} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To find the write key for your RudderStack destination in Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create a new HTTP API source in RudderStack"
        }), "\n", _jsx(_components.li, {
          children: "Navigate to Source Settings > API Keys and copy the \"Write Key\""
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.h3, {
        id: "event-types",
        children: "Event types"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track (Generic):"
          }), " Sends events of different names when a record is added, changed, or removed in the query results"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identify:"
          }), " Sends an identify event with related properties of the user"]
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "user-and-anonymous-ids",
        children: "User and anonymous IDs"
      }), "\n", _jsx(_components.p, {
        children: "it's required to send either the user or anonymous ID to RudderStack to identify the user associated with the event."
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source into properties of the user (identify) or properties of the event (track)."
      }), "\n", _jsx(_components.h3, {
        id: "sending-initial-results-track-only",
        children: "Sending initial results (track only)"
      }), "\n", _jsx(_components.p, {
        children: "For the two \"Track\" event types, Hightouch allows you to choose whether to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Send the initial set of results as track events"
        }), "\n", _jsx(_components.li, {
          children: "Or, only send the changes between the results in the subsequent syncs as track events"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Checking the \"Send events for initial result set\" checkbox will result in a large amount of events being sent in the first sync."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch will always send the initial set of results as identify calls when in the \"Identify\" event type."
      }), "\n", _jsx(_components.h3, {
        id: "event-names-and-timestamps-generic-track-only",
        children: "Event names and timestamps (generic track only)"
      }), "\n", _jsx(_components.p, {
        children: "For the generic track mode, there are further settings that are needed to determine which event name Hightouch will send to RudderStack for each added, changed, and removed record."
      }), "\n", _jsx(_components.p, {
        children: "In this example, Hightouch will send RudderStack:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A user signed up event when a record is added to the query results"
        }), "\n", _jsx(_components.li, {
          children: "A user changed subscription event when a record is changed in the query results"
        }), "\n", _jsx(_components.li, {
          children: "A user deleted account event when a record is removed in the query results"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to send a timestamp that the event occurred for generic track events. This is useful for backfilling historical events."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also allows you to send a message ID for each record to ensure that the event is deduplicated within RudderStack in the case that the same event erroneously gets sent twice."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
